import { Box } from '@mui/material';
import {
  LayoutDashboard,
  Inbox,
  Calendar,
  CalendarPlus,
  Users,
  PawPrint,
  List,
  ClipboardList,
  Microscope,
  CircleDollarSign,
  Receipt,
  BarChart3,
  BookOpen,
  Pill,
  Package,
  Shield,
  Tag,
  Target,
  Building2,
  UserCog,
  Settings,
  FileCode,
  ClipboardList as Clipboard,
  History,
  UploadCloud,
  Upload,
  Activity,
  X,
  Search,
  Banknote,
  Bell,
} from 'lucide-react';

const VetHospitalIcon = ({ size = 20 }) => (
  <Box
    sx={{
      position: 'relative',
      display: 'inline-flex',
      width: size,
      height: size,
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Building2 size={size} />
    <Box
      sx={{
        position: 'absolute',
        top: -2,
        left: '50%',
        transform: 'translateX(-50%)',
      }}
    >
      <PawPrint size={Math.round(size * 0.45)} strokeWidth={2.5} />
    </Box>
  </Box>
);

const ScalpelIcon = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    fill="currentColor"
    width={size}
    height={size}
  >
    <path d="M28.83,5.17a4.1,4.1,0,0,0-5.66,0L.34,28H9.59a5,5,0,0,0,3.53-1.46L28.83,10.83a4,4,0,0,0,0-5.66ZM12.29,18.88l2.09-2.09,2.83,2.83-2.09,2.09Zm-.58,6.24A3,3,0,0,1,9.59,26H5.17l5.71-5.71,2.83,2.83ZM27.41,9.41l-8.79,8.8-2.83-2.83,8.8-8.79a2,2,0,0,1,2.82,0,2,2,0,0,1,0,2.82Z" />
  </svg>
);

const VetXrayIcon = ({ size = 20 }) => (
  <Box
    sx={{
      position: 'relative',
      display: 'inline-flex',
      width: size,
      height: size,
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <PawPrint size={size} strokeWidth={1.5} />
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <X size={Math.round(size * 0.45)} strokeWidth={3} />
    </Box>
  </Box>
);

const CashRegisterIcon = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width={size}
    height={size}
  >
    <rect x="3" y="4" width="18" height="8" rx="2" />
    <rect x="3" y="12" width="18" height="8" rx="2" />
    <rect x="6" y="6" width="7" height="4" rx="1" />
    <circle cx="16" cy="7" r="0.8" />
    <circle cx="16" cy="10" r="0.8" />
    <circle cx="19" cy="7" r="0.8" />
    <circle cx="19" cy="10" r="0.8" />
  </svg>
);

export const drawerWidth = 240;
export const miniDrawerWidth = 72;

const roleIn = (role, allowed) => allowed.includes(role);

const allLabSubItems = [
  { text: 'Órdenes Lab', path: '/lab/orders', icon: Clipboard, roles: ['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal'] },
  { text: 'Historial Lab', path: '/lab/history', icon: History, roles: ['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal'] },
  { text: 'Carga Masiva', path: '/lab/import', icon: UploadCloud, roles: ['admin', 'Tecnólogo Médico', 'Director Médico'] },
  { text: 'Catálogo Exámenes', path: '/lab/catalog', icon: BookOpen, roles: ['admin', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal'] },
  { text: 'Catálogo Insumos', path: '/lab/supplies', icon: Package, roles: ['admin', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal'] },
];

const allImagingSubItems = [
  { text: 'Órdenes Imagen', path: '/imaging/orders', icon: Clipboard, roles: ['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Radiologo', 'Gerente Sucursal'] },
  { text: 'Historial de informes', path: '/imaging/reports-history', icon: History, roles: ['admin', 'Médico Veterinario', 'Director Médico', 'Radiologo', 'Gerente Sucursal'] },
  { text: 'Catálogo Estudios', path: '/imaging/catalog', icon: BookOpen, roles: ['admin', 'Director Médico', 'Radiologo', 'Gerente Sucursal'] },
];

const allRecepcionSubItems = [
  { text: 'Admisión', path: '/hospital/admission', icon: ClipboardList, roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
  { text: 'Caja', path: '/caja', icon: CashRegisterIcon, roles: ['admin', 'gestion', 'Gerente Sucursal', 'Supervisor de Caja'] },
  { text: 'Devoluciones', path: '/caja/devoluciones', icon: Banknote, roles: ['admin', 'gestion', 'Gerente Sucursal', 'Supervisor de Caja'] },
  { text: 'Presupuestos', path: '/presupuestos', icon: Receipt, roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
  { text: 'Informe Financiero', path: '/reportes/financiero', icon: BarChart3, roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal', 'Supervisor de Caja'] },
  { text: 'Libro de Ventas', path: '/caja/libro-ventas', icon: BookOpen, roles: ['admin', 'gestion', 'Gerente Sucursal', 'Supervisor de Caja'] },
  { text: 'Lista de Precios', path: '/caja/lista-precios', icon: Tag, roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
];

const allFarmaciaSubItems = [
  { text: 'Farmacia Principal', path: '/pharmacy', icon: Pill, roles: ['admin', 'Gerente Sucursal'] },
];

const allAdminSubItems = [
  { text: 'Aranceles', path: '/arancel-config', icon: Tag, roles: ['admin', 'Gerente Sucursal'] },
  { text: 'Configurar Metas', path: '/goals', icon: Target, roles: ['admin', 'Gerente Sucursal'] },
  { text: 'Convenios', path: '/convenios', icon: Building2, roles: ['admin', 'Gerente Sucursal'] },
  { text: 'Insumos y Medicamentos', path: '/pharmacy', icon: Pill, roles: ['admin', 'Gerente Sucursal'] },
  { text: 'Honorarios Médicos', path: '/admin/honorarios-medicos', icon: Banknote, roles: ['admin', 'Gerente Sucursal'] },
  { text: 'Notificaciones', path: '/admin/notificaciones', icon: Bell, roles: ['admin', 'Gerente Sucursal'] },
];

const allConfigSubItems = [
  { text: 'Panel Admin', path: '/admin', icon: UserCog, roles: ['admin'] },
  { text: 'Creación de Agenda', path: '/agenda-config', icon: CalendarPlus, roles: ['admin'] },
  { text: 'Configuración', path: '/settings', icon: Settings, roles: ['admin', 'Gerente Sucursal'] },
  { text: 'Codificación', path: '/codificacion', icon: FileCode, roles: ['admin'] },
];

const getLabSubItems = (role) => allLabSubItems.filter((item) => roleIn(role, item.roles));

const getImagingSubItems = (role) => allImagingSubItems.filter((item) => roleIn(role, item.roles));

const getRecepcionSubItems = (role) => allRecepcionSubItems.filter((item) => roleIn(role, item.roles));

const getAdminSubItems = (role) => allAdminSubItems.filter((item) => roleIn(role, item.roles));

const getConfigSubItems = (role) => allConfigSubItems.filter((item) => roleIn(role, item.roles));

export const showLab = (role) =>
  ['admin', 'gestion', 'Tecnólogo Médico', 'Director Médico', 'Gerente Sucursal'].includes(role);

export const showImaging = (role) =>
  ['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Radiologo', 'Gerente Sucursal'].includes(role);

export const showRecepcion = (role) =>
  ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal', 'Supervisor de Caja'].includes(role);

export const showFarmacia = (role) =>
  ['admin', 'Gerente Sucursal'].includes(role);

export const getSidebarConfig = (role, { isAdmin } = {}) => {
  if (!role) return [];

  const modules = [];

  if (roleIn(role, ['admin', 'gestion', 'Gestión Clínica', 'Director Médico', 'Gerente Sucursal'])) {
    modules.push({
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/',
      items: [],
    });
  }

  if (roleIn(role, ['admin', 'gestion', 'Director Médico', 'derivador', 'Gerente Sucursal'])) {
    modules.push({
      label: 'Derivaciones',
      icon: Inbox,
      path: '/contact-requests',
      items: [],
    });
  }

  if (roleIn(role, ['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal'])) {
    modules.push({
      label: 'Agenda',
      icon: Calendar,
      path: '/scheduler',
      items: [
        { text: 'Dashboard', icon: LayoutDashboard, path: '/agenda/dashboard', roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
        { text: 'Creación de Agendas', icon: CalendarPlus, path: '/scheduler/create', roles: ['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
      ].filter((item) => roleIn(role, item.roles)),
    });

    modules.push({
      label: 'Cirugía',
      icon: ScalpelIcon,
      path: '/surgery-scheduler',
      items: [
        { text: 'Dashboard', icon: LayoutDashboard, path: '/surgery/dashboard', roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
        { text: 'Tabla Quirúrgica', icon: List, path: '/surgery-scheduler', roles: ['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
        { text: 'Cirugía por programar', icon: ClipboardList, path: '/surgery/worklist', roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
      ].filter((item) => roleIn(role, item.roles)),
    });

    modules.push({
      label: 'Hospital',
      icon: VetHospitalIcon,
      path: '/hospital',
      items: [
        { text: 'Dashboard', icon: LayoutDashboard, path: '/hospital/dashboard', roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
        { text: 'Centro de Control', icon: Activity, path: '/hospital', roles: ['admin', 'Médico Veterinario', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
      ].filter((item) => roleIn(role, item.roles)),
    });
  }

  if (roleIn(role, ['admin', 'Médico Veterinario', 'gestion', 'Gestión Clínica', 'Director Médico', 'Gerente Sucursal'])) {
    modules.push({
      label: 'Pacientes',
      icon: PawPrint,
      path: '/patients',
      items: [
        { text: 'Tutores', icon: Users, path: '/tutors', roles: ['admin', 'gestion', 'Director Médico', 'Gerente Sucursal'] },
        { text: 'Búsqueda de casos clínicos', icon: Search, path: '/clinical-cases', roles: ['admin', 'Médico Veterinario', 'Gestión Clínica', 'Director Médico', 'Gerente Sucursal'] },
      ].filter((item) => roleIn(role, item.roles)),
    });
  }

  if (showLab(role)) {
    modules.push({
      label: 'Laboratorio',
      icon: Microscope,
      path: '/lab',
      items: getLabSubItems(role),
    });
  }

  if (showImaging(role)) {
    modules.push({
      label: 'Imágenes',
      icon: VetXrayIcon,
      path: '/imaging/orders',
      items: getImagingSubItems(role),
    });
  }

  if (showRecepcion(role)) {
    modules.push({
      label: 'Recepción y Caja',
      icon: CircleDollarSign,
      path: '/presupuestos',
      items: getRecepcionSubItems(role),
    });
  }

  if (showFarmacia(role)) {
    modules.push({
      label: 'Farmacia e Insumos',
      icon: Pill,
      path: '/pharmacy',
      items: allFarmaciaSubItems.filter((item) => roleIn(role, item.roles)),
    });
  }

  if (roleIn(role, ['admin', 'Gerente Sucursal'])) {
    modules.push({
      label: 'Administración',
      icon: Shield,
      path: '/arancel-config',
      items: getAdminSubItems(role),
    });
  }

  const configItems = getConfigSubItems(role);
  if (configItems.length > 0) {
    modules.push({
      label: 'Configuración',
      icon: Settings,
      path: '/settings',
      items: configItems,
    });
  }

  return modules;
};
